import { createSlice } from '../../../canvas/core';
import { animate } from '../../shared';
import { calculateEndRotation, getMaxEffectiveWidth } from './helpers';
import * as updaters from './state-updaters';
import type { RotationDeps, RotationSlice } from './types';

export const createRotationSlice = createSlice<RotationDeps>()<RotationSlice>(
  (set, get, _api) => ({
    rotation: {
      values: {},
      animations: {},

      selectors: {
        getBaseRotation: pageId => {
          return get().rotation.values[pageId] ?? 0;
        },

        getDisplayedRotation: pageId => {
          const state = get();
          return (
            state.rotation.animations[pageId]?.visualRotation ??
            state.rotation.selectors.getBaseRotation(pageId)
          );
        },
      },

      actions: {
        setRotation: (pageId, degrees) => {
          set(updaters.setRotationValue(pageId, degrees));
        },

        rotatePageBy: (pageId, degrees, duration = 300) => {
          const state = get();
          if (!state.pageLayout.list[pageId]) return;

          const existingCancel = state.rotation.animations[pageId]?.cancel;
          if (existingCancel) {
            existingCancel();
            set(updaters.setRotationAnimation(pageId, { cancel: undefined }));
          }

          const startRotation =
            get().rotation.selectors.getDisplayedRotation(pageId);
          const previousTarget =
            state.rotation.animations[pageId]?.targetRotation;
          const logicalRotation =
            state.rotation.selectors.getBaseRotation(pageId);
          const baseRotation = previousTarget ?? logicalRotation;
          const endRotation = calculateEndRotation(
            baseRotation,
            logicalRotation,
            degrees
          );

          const widthBefore = getMaxEffectiveWidth(get());

          set(
            updaters.setTargetWithCameraAdjustment(
              pageId,
              endRotation,
              widthBefore
            )
          );

          const cancel = animate(
            duration,
            progress => {
              const currentRotation =
                startRotation + (endRotation - startRotation) * progress;
              set(updaters.setVisualRotation(pageId, currentRotation));
            },
            () => set(updaters.commitFinalRotation(pageId, endRotation))
          );

          set(updaters.setRotationAnimation(pageId, { cancel }));
        },
      },
    },
  })
);
